import { getUrl } from '@cigna/core/router-state';
import { WindowService } from '@cigna/core/window';
import { featuresEnabled } from '@cigna/features';
import { chatQuery } from '@cigna/omni/chat-state';
import { conversationsQuery } from '@cigna/omni/conversations-state';
import { liveAvailabilityQuery } from '@cigna/omni/live-availability-state';
import { rightNowQuery } from '@cigna/omni/right-now-data-access';
import {
  chatBubbleContent,
  getChatBubbleId,
  omniConfig,
  IconDynamicColors,
  IconDynamicData,
} from '@cigna/omni/shared';
import { ScreenSize } from '@cigna/ui/responsive';
import { createSelector } from '@ngrx/store';

export const getChatBubbleData = createSelector(
  liveAvailabilityQuery.getLiveChatAvailable,
  featuresEnabled(['asyncSupportChat']),
  conversationsQuery.selectNotificationCount,
  featuresEnabled(['useRightNowChat']),
  featuresEnabled(['isEvernorthEnabled']),
  (
    liveChatAvailable,
    asyncEnabled,
    notificationCount,
    useRightNowChat,
    isEvernorthEnabled,
  ): Record<ScreenSize.XSmall | ScreenSize.Medium, IconDynamicData> => {
    const chatBubbleContentId = getChatBubbleId(
      liveChatAvailable,
      useRightNowChat ? false : asyncEnabled,
      isEvernorthEnabled,
    );
    const badgeData = {
      showNotificationBadge: useRightNowChat ? false : !!notificationCount,
      badgeCount: notificationCount,
    };
    return {
      [ScreenSize.XSmall]: {
        ...chatBubbleContent[chatBubbleContentId][ScreenSize.XSmall],
        ...badgeData,
      },
      [ScreenSize.Medium]: {
        ...chatBubbleContent[chatBubbleContentId][ScreenSize.Medium],
        ...badgeData,
      },
    };
  },
);

export const getChatBubbleContentId = createSelector(
  liveAvailabilityQuery.getLiveChatAvailable,
  featuresEnabled(['asyncSupportChat']),
  featuresEnabled(['isEvernorthEnabled']),
  getChatBubbleId,
);

export const getIconColors = createSelector(
  liveAvailabilityQuery.getLiveChatAvailable,
  featuresEnabled(['asyncSupportChat']),
  featuresEnabled(['useRightNowChat']),
  featuresEnabled(['isEvernorthEnabled']),
  featuresEnabled(['showNewChatBubble']),
  (
    liveChatAvailable,
    asyncEnabled,
    useRightNowChat,
    isEvernorthEnabled,
    shouldShowNewChatBubble,
  ): IconDynamicColors => {
    if (!liveChatAvailable && shouldShowNewChatBubble) {
      return {
        color: omniConfig.omniColors.gray80,
      };
    }
    if (!liveChatAvailable && (!asyncEnabled || useRightNowChat)) {
      return {
        color: omniConfig.omniColors.white,
        textColor: omniConfig.omniColors.gray60,
        strokeColor: omniConfig.omniColors.gray40,
      };
    }
    if (isEvernorthEnabled) {
      return {
        color: omniConfig.omniColors.audiBlack,
        textColor: omniConfig.omniColors.white,
        strokeColor: omniConfig.omniColors.white,
        dropShadow: 'drop-shadow(0 0 4px white)',
      };
    }
    return {
      // default values for chat bub color
      color: omniConfig.omniColors.digitalGreen,
      textColor: omniConfig.omniColors.white,
    };
  },
);

/**
 * Add a page name that would match part of a uri
 * To exclude the bubble from showing up
 *
 * example:
 * ['directory']
 */
export const exclusionList: string[] = [
  'secure/consumer/elevate/assessment/start',
];
export const inclusionList: string[] = [];

export const isOnExcludedPage = (_window: WindowService) =>
  createSelector(getUrl, (url) =>
    exclusionList.some((fragment) =>
      (url || _window.location.href).includes(fragment),
    ),
  );

export const isOnInclusionPage = (_window: WindowService) =>
  createSelector(getUrl, (url) =>
    inclusionList.some((fragment) =>
      (url || _window.location.href).includes(fragment),
    ),
  );

export const showChatBubble = (_window: WindowService) =>
  createSelector(
    isOnExcludedPage(_window),
    isOnInclusionPage(_window),
    liveAvailabilityQuery.getLiveAvailabilityLoaded,
    chatQuery.isChatClosed,
    featuresEnabled(['generalChat']),
    featuresEnabled(['useRightNowChat']),
    rightNowQuery.isWidgetInitialized,
    featuresEnabled(['showNewChatBubble']),
    featuresEnabled(['hasRestrictedChat']),
    featuresEnabled(['shouldEnableChat', 'canDisplayRestrictedBubble']),
    (
      onExcludedPage,
      onInclusionPage,
      liveAvailabilityLoaded,
      isChatClosed,
      isGrubbleEnabled,
      useRightNowChat,
      isRightNowWidgetInitialized,
      showNewChatBubble,
      hasRestrictedChat,
      displayRestrictedChat,
    ) =>
      !onExcludedPage &&
      (!hasRestrictedChat || onInclusionPage || displayRestrictedChat) &&
      liveAvailabilityLoaded &&
      (isChatClosed || showNewChatBubble) &&
      isGrubbleEnabled &&
      (!useRightNowChat || isRightNowWidgetInitialized),
  );
